import { useState, useEffect, useCallback } from 'react';
import { apiFetch, getAccessToken } from '../lib/api.js';
import { useOrg } from '../lib/org-context.js';
import { useToast } from '../components/Toast.js';
import { SectionCard } from '../components/website/SectionCard.js';
import { BrandColorsSection } from '../components/website/BrandColorsSection.js';
import { AboutSection } from '../components/website/AboutSection.js';
import { FaqSection } from '../components/website/FaqSection.js';
import { ScheduleSection } from '../components/website/ScheduleSection.js';
import { CrudSection, type FieldDef } from '../components/website/CrudSection.js';

type Tab = 'content' | 'edit' | 'requests';
type EditMode = 'ai' | '98';
type RequestStatus = 'pending' | 'in_progress' | 'completed' | 'rejected';

interface EditRequest {
  id: string; page_url: string; mode: EditMode; instructions: string;
  status: RequestStatus; ai_suggestion?: string; requested_at: string;
}

interface SiteContent {
  brand: Record<string, string> | null;
  about: string | null; mission: string | null;
  values: Array<{ title: string; description: string }>; stats: Array<{ label: string; value: string }>;
  faq: Array<{ question: string; answer: string }>; schedule: { timezone?: string; classes?: Array<Record<string, unknown>> } | null;
}

// Field definitions for CRUD sections
const TESTIMONIAL_FIELDS: FieldDef[] = [
  { key: 'author_name', label: 'Name', type: 'text', placeholder: 'John Smith', required: true },
  { key: 'author_role', label: 'Role (optional)', type: 'text', placeholder: 'Parent of student' },
  { key: 'quote', label: 'Testimonial', type: 'textarea', placeholder: 'What they said about your studio...', required: true },
  { key: 'rating', label: 'Rating (1-5)', type: 'number' },
  { key: 'photo_url', label: 'Photo URL (optional)', type: 'url', placeholder: 'https://...' },
];

const INSTRUCTOR_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Name', type: 'text', placeholder: 'Sensei Mike', required: true },
  { key: 'role', label: 'Role', type: 'text', placeholder: 'Head Instructor' },
  { key: 'bio', label: 'Bio', type: 'textarea', placeholder: '20 years of experience...' },
  { key: 'photo_url', label: 'Photo URL (optional)', type: 'url', placeholder: 'https://...' },
];

const OFFER_FIELDS: FieldDef[] = [
  { key: 'title', label: 'Offer Title', type: 'text', placeholder: 'Free Trial Week', required: true },
  { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Try any class free for a week...' },
  { key: 'price_display', label: 'Price Display', type: 'text', placeholder: '$0 / Free' },
  { key: 'cta_text', label: 'Button Text', type: 'text', placeholder: 'Get Started' },
  { key: 'is_active', label: 'Active', type: 'toggle' },
];

export function MyWebsitePage() {
  const { viewingOrgId } = useOrg();
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>('content');

  const [siteUrl, setSiteUrl] = useState<string | null>(null);
  const [_orgSlug, setOrgSlug] = useState<string | null>(null);
  const [siteContent, setSiteContent] = useState<SiteContent | null>(null);
  const [requests, setRequests] = useState<EditRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Edit flow state
  const [selectedPage, setSelectedPage] = useState('');
  const [mode, setMode] = useState<EditMode | null>(null);
  const [instructions, setInstructions] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);
  const [editDone, setEditDone] = useState(false);

  const orgId = viewingOrgId ?? (() => {
    try { const t = getAccessToken(); if (!t) return null; return JSON.parse(atob(t.split('.')[1] ?? '')).org_id ?? null; }
    catch { return null; }
  })();

  const fetchData = useCallback(async () => {
    if (!orgId) return;
    setIsLoading(true);
    try {
      const [orgRes, contentRes, reqRes] = await Promise.all([
        apiFetch<{ slug: string; primaryDomain: string | null }>(`/orgs/${orgId}`),
        apiFetch<SiteContent>(`/orgs/${orgId}/site-content`).catch(() => null),
        apiFetch<{ requests: EditRequest[] }>(`/orgs/${orgId}/site-edit-requests`).catch(() => ({ requests: [] })),
      ]);
      setOrgSlug(orgRes.slug);
      setSiteUrl(orgRes.primaryDomain ? `https://${orgRes.primaryDomain}` : `https://${orgRes.slug}.membies.com`);
      setSiteContent(contentRes);
      setRequests(reqRes.requests);
    } catch { /* ignore */ }
    finally { setIsLoading(false); }
  }, [orgId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetEditFlow = () => { setSelectedPage(''); setMode(null); setInstructions(''); setAiSuggestion(null); setEditDone(false); };

  if (isLoading || !orgId) {
    return <div className="max-w-2xl space-y-4">{[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl border border-gray-100 p-6 animate-pulse"><div className="h-4 bg-gray-200 rounded w-32 mb-3" /><div className="h-8 bg-gray-100 rounded w-full" /></div>)}</div>;
  }

  return (
    <div className="max-w-2xl space-y-4">
      {/* Domain banner */}
      {siteUrl && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Your Website</p>
            <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-primary-600 hover:text-primary-800 underline decoration-primary-200">
              {siteUrl.replace('https://', '')}
            </a>
          </div>
          <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors">Visit Site</a>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {(['content', 'edit', 'requests'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors capitalize ${
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {t === 'edit' ? 'Edit Website' : t === 'requests' ? `Requests ${requests.length > 0 ? `(${requests.length})` : ''}` : 'Content'}
          </button>
        ))}
      </div>

      {/* ===== CONTENT TAB ===== */}
      {tab === 'content' && siteContent && (
        <div className="space-y-3">
          <SectionCard icon="🎨" title="Brand Colors" subtitle="Colors and fonts for your site" defaultOpen>
            <BrandColorsSection orgId={orgId} initial={siteContent.brand as Record<string, string> | null} onSaved={fetchData} />
          </SectionCard>

          <SectionCard icon="🏷️" title="Special Offers" subtitle="Promotions and trial offers">
            <CrudSection orgId={orgId} endpoint="offers" fields={OFFER_FIELDS} displayField="title" subtitleField="price_display" />
          </SectionCard>

          <SectionCard icon="📋" title="Programs" subtitle="Manage your programs and classes">
            <div className="text-center py-4">
              <a href="/app/programs" className="text-sm text-primary-600 hover:text-primary-800 font-medium underline">
                Go to Programs page →
              </a>
            </div>
          </SectionCard>

          <SectionCard icon="⭐" title="Testimonials" subtitle="What your members say">
            <CrudSection orgId={orgId} endpoint="testimonials" fields={TESTIMONIAL_FIELDS} displayField="author_name" subtitleField="quote" />
          </SectionCard>

          <SectionCard icon="👥" title="Instructors" subtitle="Your coaching team">
            <CrudSection orgId={orgId} endpoint="instructors" fields={INSTRUCTOR_FIELDS} displayField="name" subtitleField="role" />
          </SectionCard>

          <SectionCard icon="📖" title="Our Story" subtitle="About your studio">
            <AboutSection orgId={orgId} initial={{ about: siteContent.about ?? '', mission: siteContent.mission ?? '', values: siteContent.values, stats: siteContent.stats }} onSaved={fetchData} />
          </SectionCard>

          <SectionCard icon="📅" title="Class Schedule" subtitle="When classes happen">
            <ScheduleSection orgId={orgId} initial={siteContent.schedule as { timezone?: string; classes?: Array<{ name: string; days: string[]; time: string; instructor?: string; level?: string }> } | null} onSaved={fetchData} />
          </SectionCard>

          <SectionCard icon="❓" title="FAQ" subtitle="Frequently asked questions">
            <FaqSection orgId={orgId} initial={siteContent.faq} onSaved={fetchData} />
          </SectionCard>
        </div>
      )}

      {/* ===== EDIT WEBSITE TAB ===== */}
      {tab === 'edit' && (
        <div className="space-y-4">
          {!mode && !editDone && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
              <div><h2 className="text-lg font-semibold text-gray-900">Edit Your Website</h2><p className="text-sm text-gray-500 mt-1">Choose a page, then tell us what to change.</p></div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Select a page</label>
                <select value={selectedPage} onChange={e => setSelectedPage(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 bg-white">
                  <option value="">Choose a page...</option>
                  {siteUrl && [
                    { label: 'Homepage', url: siteUrl + '/' },
                    { label: 'Contact', url: siteUrl + '/contact/' },
                    { label: 'All Programs', url: siteUrl + '/classes/' },
                    { label: 'About', url: siteUrl + '/about/' },
                  ].map(p => <option key={p.url} value={p.url}>{p.label}</option>)}
                  <option value="__custom">Other (enter URL)</option>
                </select>
              </div>
              {selectedPage && selectedPage !== '__custom' && (
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <button onClick={() => setMode('ai')} className="p-4 rounded-xl border-2 border-primary-200 bg-primary-50 hover:border-primary-400 transition-colors text-left">
                    <p className="text-sm font-semibold text-primary-900">AI Edit</p><p className="text-xs text-primary-700 mt-1">Instant. AI rewrites and you approve.</p>
                  </button>
                  <button onClick={() => setMode('98')} className="p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors text-left">
                    <p className="text-sm font-semibold text-gray-900">98 Edit</p><p className="text-xs text-gray-500 mt-1">Our team. Response within 48 hours.</p>
                  </button>
                </div>
              )}
            </div>
          )}
          {mode && !aiSuggestion && !editDone && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
              <div className="flex items-center gap-2">
                <button onClick={resetEditFlow} className="text-gray-400 hover:text-gray-600"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg></button>
                <div><h2 className="text-lg font-semibold text-gray-900">{mode === 'ai' ? 'AI Edit' : '98 Edit'}</h2><p className="text-xs text-gray-400 truncate">{selectedPage}</p></div>
              </div>
              <textarea value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="Describe what to change..." rows={5}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-vertical" />
              <button onClick={async () => {
                setIsProcessing(true);
                try {
                  if (mode === 'ai') {
                    const res = await apiFetch<{ suggestion: string }>(`/orgs/${orgId}/site-edit-requests`, { method: 'POST', body: JSON.stringify({ page_url: selectedPage, mode: 'ai', instructions }) });
                    setAiSuggestion(res.suggestion);
                  } else {
                    await apiFetch(`/orgs/${orgId}/site-edit-requests`, { method: 'POST', body: JSON.stringify({ page_url: selectedPage, mode: '98', instructions }) });
                    toast('success', 'Request submitted — response within 48 hours'); resetEditFlow(); fetchData();
                  }
                } catch { toast('error', 'Failed'); } finally { setIsProcessing(false); }
              }} disabled={isProcessing || !instructions.trim()}
                className="w-full px-4 py-3 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                {isProcessing ? 'Processing...' : mode === 'ai' ? 'Generate Edit' : 'Submit to 98 Team'}
              </button>
            </div>
          )}
          {aiSuggestion && !editDone && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Review AI Edit</h2>
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap border border-gray-200">{aiSuggestion}</div>
              <div className="flex gap-3">
                <button onClick={async () => {
                  setIsProcessing(true);
                  try { await apiFetch(`/orgs/${orgId}/site-edit-requests/confirm`, { method: 'POST', body: JSON.stringify({ page_url: selectedPage, instructions, confirmed_content: aiSuggestion }) }); setEditDone(true); toast('success', 'Edit applied!'); fetchData(); }
                  catch { toast('error', 'Failed'); } finally { setIsProcessing(false); }
                }} disabled={isProcessing} className="flex-1 px-4 py-3 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                  {isProcessing ? 'Applying...' : 'Confirm & Apply'}
                </button>
                <button onClick={() => setAiSuggestion(null)} className="px-4 py-3 text-sm text-gray-500">Try Again</button>
              </div>
            </div>
          )}
          {editDone && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 text-center space-y-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto"><svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></div>
              <h3 className="text-lg font-semibold text-gray-900">Edit Applied!</h3>
              <div className="flex gap-3 justify-center">
                <a href={selectedPage} target="_blank" rel="noopener noreferrer" className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700">Preview</a>
                <button onClick={resetEditFlow} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Another Edit</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== REQUESTS TAB ===== */}
      {tab === 'requests' && (
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-900">Edit Requests</h3></div>
          {requests.length === 0 ? (
            <div className="p-8 text-center"><p className="text-sm text-gray-500">No requests yet.</p></div>
          ) : (
            <div className="divide-y divide-gray-50">
              {requests.map(r => (
                <div key={r.id} className="px-5 py-3 space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${r.mode === 'ai' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{r.mode === 'ai' ? 'AI' : '98'}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        r.status === 'completed' ? 'bg-green-100 text-green-700' : r.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                      }`}>{r.status}</span>
                    </div>
                    <span className="text-xs text-gray-400">{new Date(r.requested_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-gray-700">{r.instructions}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
